import CommonTitleBar from "components/common/CommonTitleBar";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import Filters from "components/common/Filters";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import {
  formatDate,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import {
  demoRequestStatusOptions,
  orderByOptions,
  statusOptions,
} from "components/constants/StatusKeys";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import { Ellipsis } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteDemoRequest,
  getDemoRequest,
  updateDemoRequestStatus,
} from "store/actions/globalSettingSlice";
import { showAlert } from "store/reducers/alertReducer";
import {
  resetDemoRequestFilterState,
  updateDemoRequestFilterState,
} from "store/reducers/globalSettingReducer";

const DemoRequest = () => {
  const { t } = useTranslation();
  document.title = t("Demo Request | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    users,
    userDetails,
    gettingDetails,
    demoRequestsFilter,
    demoRequest,
  } = useSelector((state) => state.GlobalSettings);

  const { keyword, dates, selectedFilters } = demoRequestsFilter;

  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [modalType, setModalType] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [show, setShow] = useState(false); 
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { orderBy, status } = selectedFilters;

    const isReset = actionType === "RESET";
    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? "all" : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };
    dispatch(getDemoRequest(payload));
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
    };

    dispatch(deleteDemoRequest(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  // Update setters to dispatch actions:
  const setKeyword = (value) =>
    dispatch(updateDemoRequestFilterState({ keyword: value }));

  const setDates = (value) =>
    dispatch(updateDemoRequestFilterState({ dates: value }));

  const setSelectedFilters = (value) => {
    const updatedFilters =
      typeof value === "function" ? value(selectedFilters) : value;
    dispatch(updateDemoRequestFilterState({ selectedFilters: updatedFilters }));
  };

  const filters = [
    {
      key: "status",
      placeholder: "Status",
      options: demoRequestStatusOptions,
      type: "singleSelect",
    },
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
  ];

  const handleReset = () => {
    dispatch(resetDemoRequestFilterState());
    getAllData(1, { actionType: "RESET" });
  };

  const handleShow = (id) => {
    setSelectedId(id);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedStatus("");
    setSelectedId(null);
  };

  const handleUpdateStatus = () => {
    console.log("Selected ID:", selectedId);
    console.log("New Status:", selectedStatus);

    const payload = {
      id: selectedId,
      status: selectedStatus,
    };

    dispatch(updateDemoRequestStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          handleClose();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <CommonTitleBar title={t("Demo Requests")} />

          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={9} />
                ) : demoRequest.length === 0 ? (
                  <NoDataFound text={t("No demo requests found")} />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 200 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>#</th>
                          <th>{t("Full Name")}</th>
                          <th>{t("Email")}</th>
                          <th>{t("Phone")}</th>
                          <th>{t("Company Name")}</th>
                          <th>{t("Message")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Created At")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {demoRequest?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item?.fullName || "-"}</td>
                            <td>{item?.email || "-"}</td>
                            <td>
                              {item?.phoneCode} {item?.phoneNumber}
                            </td>
                            <td>{item?.companyName || "-"}</td>
                            <td>
                              <p>{item?.message || "-"}</p>
                            </td>
                            <td>
                              <p
                                className={`status_tag  ${
                                  demoRequestStatusOptions?.find(
                                    (op) => op.value === item?.status
                                  )?.className
                                }`}
                              >
                                {demoRequestStatusOptions?.find(
                                  (op) => op.value === item?.status
                                )?.name || ""}
                              </p>
                            </td>
                            <td>{formatDate(item?.createdAt)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    {item?.status !== "completed" && (
                                      <Dropdown.Item
                                        href="#"
                                        onClick={() => {
                                          setSelectedStatus(item?.status);
                                          handleShow(item._id);
                                        }}
                                      >
                                        {t("Update Status")}
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Demo Request",
        })}
        isLoading={isLoading}
      />

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Update Status")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("Select Status")}</Form.Label>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option hidden>{t("Select")}</option>
              {demoRequestStatusOptions
                .filter((op) => op.value !== "all") // hide "All"
                .map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="main_btn"
            disabled={!selectedStatus || isLoading}
            onClick={handleUpdateStatus}
          >
            {t("Update")}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default DemoRequest;
